import React, { useEffect, useRef, useState, useCallback } from 'react';
import { extractVideoId } from '../../services/youtubeService';
import usePlayerStateMonitor from './hooks/usePlayerStateMonitor';

/**
 * CNN-style YouTube Shorts Player
 * Uses a single persistent YouTube player instance to maintain volume state across videos
 * Based on the proven pattern that CNN uses for their shorts implementation
 */

// LocalStorage keys for audio preferences
const STORAGE_KEY_UNLOCKED = 'shorts_hasUserUnlockedSound';
const STORAGE_KEY_MUTE_PREF = 'shorts_lastUserMutePreference';

// Load audio preferences from localStorage
const loadAudioPreferences = () => {
  try {
    const unlocked = localStorage.getItem(STORAGE_KEY_UNLOCKED);
    const mutePref = localStorage.getItem(STORAGE_KEY_MUTE_PREF);
    
    return {
      hasUserUnlockedSound: unlocked === 'true',
      lastUserMutePreference: mutePref !== null ? mutePref === 'true' : true
    };
  } catch (e) {
    console.warn('Failed to load audio preferences from localStorage:', e);
    return {
      hasUserUnlockedSound: false,
      lastUserMutePreference: true
    };
  }
};

// Save audio preferences to localStorage
const saveAudioPreferences = (hasUnlocked, mutePref) => {
  try {
    localStorage.setItem(STORAGE_KEY_UNLOCKED, hasUnlocked.toString());
    localStorage.setItem(STORAGE_KEY_MUTE_PREF, mutePref.toString());
  } catch (e) {
    console.warn('Failed to save audio preferences to localStorage:', e);
  }
};

// Initialize audio preferences from localStorage
const savedPrefs = loadAudioPreferences();

// Global state to maintain across component re-renders
let player = null;
let hasUserUnlockedSound = savedPrefs.hasUserUnlockedSound;
let isPlayerReady = false;
let lastUserMutePreference = savedPrefs.lastUserMutePreference;
let actualMuteState = lastUserMutePreference; // Initialize based on user preference
let isFirstVideoSincePageLoad = true; // Track first video to prevent mobile autoplay issues
let sessionUserInteraction = false; // Track if user has interacted in this session

// Expose to window for debugging
if (typeof window !== 'undefined') {
  window.player = null;
  window.hasUserUnlockedSound = hasUserUnlockedSound;
  window.lastUserMutePreference = lastUserMutePreference;
  window.actualMuteState = actualMuteState;
  window.isFirstVideoSincePageLoad = isFirstVideoSincePageLoad;
  window.sessionUserInteraction = sessionUserInteraction;
  window.playerDebugInfo = {};
}

// Helper function to detect mobile devices
const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
};

const ShortsPlayerYouTube = ({ 
  shortData, 
  isPlaying, 
  isMuted, 
  onPlayStateChange,
  onMuteStateChange,
  onNextVideo,
  onPrevVideo,
  onFirstInteraction 
}) => {
  const containerRef = useRef(null);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const progressIntervalRef = useRef(null);
  
  // Extract video ID from the shorts URL
  const videoId = shortData ? extractVideoId(shortData.shortUrl) : null;
  
  // Debug logging function
  const debugLog = useCallback((action, details = {}) => {
    const info = {
      timestamp: new Date().toISOString(),
      action,
      videoId,
      shortUrl: shortData?.shortUrl,
      series: shortData?.series,
      playerState: player?.getPlayerState?.() || 'N/A',
      apiMuted: player?.isMuted?.() || 'N/A',
      actualMuteState,
      hasUserUnlockedSound,
      lastUserMutePreference,
      isFirstVideoSincePageLoad,
      sessionUserInteraction,
      isMobile: isMobileDevice(),
      isPlaying,
      isMuted,
      ...details
    };
    
    console.log(`[PLAYER DEBUG] ${action}:`, info);
    
    // Store in window for debugging
    window.playerDebugInfo = info;
  }, [videoId, shortData, isPlaying, isMuted]);

  // Progress tracking functions
  const stopProgressTracking = useCallback(() => {
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  }, []);

  const startProgressTracking = useCallback(() => {
    if (progressIntervalRef.current) return;
    
    progressIntervalRef.current = setInterval(() => {
      if (player && player.getDuration && player.getCurrentTime) {
        try {
          const currentTime = player.getCurrentTime();
          const totalDuration = player.getDuration();
          if (totalDuration > 0) {
            const progressPercent = (currentTime / totalDuration) * 100;
            setProgress(progressPercent);
            setDuration(totalDuration);
          }
        } catch (err) {
          // Silent fail during tracking
        }
      }
    }, 100);
  }, []);

  // Safe player method caller with retry logic for critical operations
  const safePlayerCall = useCallback((methodName, ...args) => {
    if (!player) {
      console.warn(`Player not initialized for ${methodName}`);
      return false;
    }
    
    if (typeof player[methodName] !== 'function') {
      console.warn(`Player method ${methodName} not available yet`);
      
      // Schedule retry for critical unmute operations
      if (methodName === 'unMute' && hasUserUnlockedSound && !lastUserMutePreference) {
        setTimeout(() => {
          if (player && typeof player.unMute === 'function') {
            console.log('Retrying unMute after delay');
            player.unMute();
            player.setVolume(75);
          }
        }, 100);
      }
      return false;
    }
    
    // Method exists, call it safely
    try {
      player[methodName](...args);
      return true;
    } catch (err) {
      console.error(`Error calling player.${methodName}:`, err);
      return false;
    }
  }, []);

  // Initialize YouTube IFrame API
  useEffect(() => {
    console.log('ShortsPlayerYouTube component mounted - isFirstVideoSincePageLoad:', isFirstVideoSincePageLoad);
    
    // Load YouTube IFrame API script if not already loaded
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    // Set up the callback for when API is ready
    window.onYouTubeIframeAPIReady = () => {
      console.log('YouTube IFrame API ready');
      if (containerRef.current && !player) {
        initializePlayer();
      }
    };

    // If API is already loaded, initialize immediately
    if (window.YT && window.YT.Player && containerRef.current && !player) {
      initializePlayer();
    }
  }, []);

  // Cleanup effect - destroy player ONLY on true component unmount
  useEffect(() => {
    return () => {
      // Cleanup when component unmounts
      if (player) {
        console.log('Destroying YouTube player on unmount');
        // Call stopProgressTracking directly without dependency
        if (progressIntervalRef.current) {
          clearInterval(progressIntervalRef.current);
          progressIntervalRef.current = null;
        }
        if (player.destroy) {
          player.destroy();
        }
        player = null;
        window.player = null;
        isPlayerReady = false;
        
        // IMPORTANT: Reset first video flag and session interaction when component unmounts
        // This ensures videos start muted when user returns to the viewer
        isFirstVideoSincePageLoad = true;
        sessionUserInteraction = false;
        window.isFirstVideoSincePageLoad = true;
        window.sessionUserInteraction = false;
        console.log('Component unmounting - reset isFirstVideoSincePageLoad and sessionUserInteraction');
      }
    };
  }, []); // Empty dependency array - only run on mount/unmount
  
  // Monitor player state changes for debugging
  usePlayerStateMonitor(player, isReady, process.env.NODE_ENV === 'development');

  // Initialize the player (only once)
  const initializePlayer = useCallback(() => {
    // Check if player exists but is invalid/destroyed
    if (player && (!player.getIframe || !player.getIframe())) {
      console.log('Detected stale player instance, resetting...');
      player = null;
      window.player = null;
      isPlayerReady = false;
    }
    
    if (!videoId || player) return;

    console.log('Initializing YouTube player with video:', videoId);
    
    const playerConfig = {
      width: '100%',
      height: '100%',
      videoId: videoId,
      playerVars: {
        autoplay: 1,          // Start automatically
        mute: 1,              // ALWAYS start muted for browser autoplay - user must click unmute
        playsinline: 1,       // Play inline on mobile
        controls: 0,          // Hide YouTube controls
        rel: 0,               // Don't show related videos
        enablejsapi: 1,       // Enable API control
        modestbranding: 1,    // Minimal YouTube branding
        fs: 0,                // Disable fullscreen button
        iv_load_policy: 3,    // Hide annotations
        disablekb: 1,         // Disable keyboard controls
        origin: window.location.origin.replace('https:', 'http:')
      },
      events: {
        onReady: onPlayerReady,
        onStateChange: onPlayerStateChange,
        onError: onPlayerError,
        onAutoplayBlocked: () => {
          console.log('Autoplay blocked by browser');
          
          const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
          const isAndroid = /Android/.test(navigator.userAgent);
          
          // Simple, accurate message
          let errorMsg = 'Tap to play';
          let errorDetail = '';
          
          if (isIOS) {
            errorDetail = 'Check if Low Power Mode is on';
          } else if (isAndroid) {
            errorDetail = 'Check Battery Saver settings';
          }
          
          setError({
            type: 'autoplay-blocked',
            message: errorMsg,
            detail: errorDetail
          });
        }
      }
    };
    
    player = new window.YT.Player(containerRef.current, playerConfig);
    
    // Update window reference for debugging
    window.player = player;
  }, []);

  // Player ready handler
  const onPlayerReady = () => {
    debugLog('Player ready');
    console.log('Player instance:', player);
    
    // Validate player has all required methods
    const requiredMethods = ['playVideo', 'pauseVideo', 'mute', 'unMute', 'setVolume', 'isMuted', 
                            'loadVideoById', 'cueVideoById', 'seekTo', 'getPlayerState', 'getCurrentTime', 
                            'getDuration', 'getVideoData'];
    
    const missingMethods = requiredMethods.filter(method => typeof player[method] !== 'function');
    
    if (missingMethods.length > 0) {
      console.error('Player missing required methods:', missingMethods);
      setError('Player initialization incomplete');
      return;
    }
    
    console.log('Player ready with required methods');
    isPlayerReady = true;
    setIsReady(true);
    setError(null);
    
    // Add allow attribute to iframe for better mobile support
    const iframe = player.getIframe();
    if (iframe) {
      iframe.setAttribute('allow', 'autoplay; fullscreen; encrypted-media');
      iframe.setAttribute('playsinline', '1');
      debugLog('Added allow attribute to iframe');
    }
    
    // Note: We'll capture user interaction through our custom mute button
    // instead of trying to access the iframe's contentWindow (cross-origin issue)
    
    // Apply initial play state
    if (isPlaying) {
      player.playVideo();
    }
    
    // IMPORTANT: Do NOT unmute videos automatically on mobile - this causes pause issues
    // The user must click the unmute button to trigger sound with a user gesture
    // We only sync the parent component state here
    // Also, don't sync unmuted state on first video after mount to prevent autoplay issues
    // NEW: Only sync unmuted preference if user has interacted in this session
    if (hasUserUnlockedSound && sessionUserInteraction && onMuteStateChange && isMuted !== lastUserMutePreference) {
      // If it's the first video after mount and saved preference is unmuted, 
      // don't sync - keep it muted to ensure autoplay works
      if (isFirstVideoSincePageLoad && !lastUserMutePreference) {
        debugLog('First video after mount - keeping muted despite saved preference', {
          isMuted,
          lastUserMutePreference,
          isFirstVideoSincePageLoad,
          sessionUserInteraction
        });
      } else {
        debugLog('Syncing parent component with saved preference', {
          isMuted,
          lastUserMutePreference,
          sessionUserInteraction
        });
        onMuteStateChange(lastUserMutePreference);
      }
    }
  };

  // Handle state changes
  const onPlayerStateChange = (event) => {
    // State constants
    const UNSTARTED = -1;
    const ENDED = 0;
    const PLAYING = 1;
    const PAUSED = 2;
    const CUED = 5;
    
    debugLog('Player state changed', { 
      state: event.data,
      stateName: event.data === PLAYING ? 'PLAYING' : 
                 event.data === PAUSED ? 'PAUSED' : 
                 event.data === ENDED ? 'ENDED' : 
                 event.data === CUED ? 'CUED' : 
                 event.data === UNSTARTED ? 'UNSTARTED' : 'UNKNOWN'
    });
    
    switch (event.data) {
      case CUED:
      case UNSTARTED:
        // Video is ready to play - ensure it's muted and play it
        console.log('Video CUED/UNSTARTED - hasUserUnlockedSound:', hasUserUnlockedSound, 'isMuted:', isMuted, 'isFirstVideo:', isFirstVideoSincePageLoad, 'isMobile:', isMobileDevice());
        
        // ALWAYS ensure video is muted and playing for reliable autoplay
        console.log('Ensuring video is muted and playing');
        safePlayerCall('mute');
        safePlayerCall('playVideo');
        
        // Mark that first video has been processed
        if (isFirstVideoSincePageLoad) {
          isFirstVideoSincePageLoad = false;
          window.isFirstVideoSincePageLoad = false;
          console.log('First video after mount processed, subsequent videos will respect preferences');
        }
        break;
        
      case PLAYING:
        onPlayStateChange(true);
        startProgressTracking();
        
        // Don't automatically unmute in PLAYING state - this causes mobile browsers to pause
        // The user's mute preference is handled through the mute button handler only
        if (player && typeof player.isMuted === 'function') {
          try {
            const actuallyMuted = player.isMuted();
            console.log('PLAYING state - Player actually muted:', actuallyMuted, 'lastUserMutePreference:', lastUserMutePreference, 'hasUserUnlockedSound:', hasUserUnlockedSound);
          } catch (err) {
            console.warn('Error checking mute state in PLAYING handler:', err);
          }
        }
        break;
        
      case PAUSED:
        onPlayStateChange(false);
        stopProgressTracking();
        break;
        
      case ENDED:
        console.log('Video ended, auto-advancing...');
        stopProgressTracking();
        onPlayStateChange(false);
        // Auto-advance to next video
        setTimeout(() => {
          if (onNextVideo) {
            onNextVideo();
          }
        }, 100);
        break;
        
      default:
        // Other states we don't need to handle
        break;
    }
  };

  // Handle errors
  const onPlayerError = (event) => {
    console.error('YouTube player error:', event.data);
    setError('Video unavailable');
    setIsReady(false);
  };


  // Load new video when shortData changes
  useEffect(() => {
    if (!videoId) return;
    
    // If no player exists, try to initialize
    if (!player && containerRef.current && window.YT && window.YT.Player) {
      debugLog('No player found when video changed, initializing');
      initializePlayer();
      return;
    }
    
    if (!player || !isPlayerReady) return;
    
    const currentVideoId = player.getVideoData?.()?.video_id || null;
    
    if (currentVideoId !== videoId) {
      debugLog('Loading new video', { 
        oldVideoId: currentVideoId,
        newVideoId: videoId,
        series: shortData?.series
      });
      setProgress(0);
      setDuration(0);
      
      // Don't reset mute state - preserve user's preference
      // actualMuteState will be managed by the user's mute button
      
      player.loadVideoById(videoId, 0);
    }
  }, [videoId, debugLog]);

  // Handle play/pause from parent
  useEffect(() => {
    if (!player || !isPlayerReady) return;
    
    try {
      if (player && typeof player.getPlayerState === 'function') {
        const playerState = player.getPlayerState();
        const isCurrentlyPlaying = playerState === 1; // PLAYING
        
        if (isPlaying && !isCurrentlyPlaying) {
          safePlayerCall('playVideo');
        } else if (!isPlaying && isCurrentlyPlaying) {
          safePlayerCall('pauseVideo');
        }
      }
    } catch (err) {
      console.warn('Error controlling playback:', err);
    }
  }, [isPlaying]);

  // Handle mute/unmute from parent
  useEffect(() => {
    if (!player || !isPlayerReady) return;
    
    // Special handling for mobile first video - only block automatic unmuting, not user clicks
    if (isMobileDevice() && isFirstVideoSincePageLoad) {
      debugLog('Mobile first video - special handling', {
        isMuted,
        hasUserUnlockedSound,
        lastUserMutePreference
      });
      
      // If user has unlocked sound (clicked unmute button), allow the state change
      if (hasUserUnlockedSound && !isMuted) {
        debugLog('Mobile first video - user clicked unmute, allowing state change');
        // Let the normal flow continue to unmute the video
      } else if (!isMuted && onMuteStateChange) {
        // Force parent to show muted state for first video on mobile (automatic only)
        debugLog('Mobile first video - forcing muted state for automatic behavior');
        onMuteStateChange(true);
        return;
      } else {
        // Keep muted for mobile first video
        debugLog('Mobile first video - keeping muted');
        return;
      }
    }
    
    // Check if parent state matches our saved preference
    // This helps sync the state when switching categories
    // IMPORTANT: Only apply saved unmuted preference if user has interacted in this session
    const shouldBeMuted = (hasUserUnlockedSound && sessionUserInteraction) ? lastUserMutePreference : true;
    
    debugLog('Parent mute state change', { 
      parentMuted: isMuted,
      shouldBeMuted,
      actualMuteState,
      hasUserUnlockedSound,
      lastUserMutePreference,
      sessionUserInteraction
    });
    
    // Only update if there's a mismatch
    if (isMuted !== shouldBeMuted && onMuteStateChange) {
      debugLog('Syncing parent state with saved preference');
      onMuteStateChange(shouldBeMuted);
      return; // Let the parent update trigger this effect again
    }
    
    try {
      if (isMuted) {
        debugLog('Muting from parent');
        player.mute();
        actualMuteState = true;
        window.actualMuteState = true;
      } else if (hasUserUnlockedSound && sessionUserInteraction) {
        debugLog('Unmuting from parent - session interaction confirmed');
        player.unMute();
        player.setVolume(75);
        actualMuteState = false;
        window.actualMuteState = false;
        
        // Check if video paused after unmute
        setTimeout(() => {
          if (player && typeof player.getPlayerState === 'function') {
            const state = player.getPlayerState();
            if (state !== 1 && isPlaying) {
              debugLog('Video paused after parent unmute, resuming');
              player.playVideo();
            }
          }
        }, 100);
      }
    } catch (err) {
      console.error('Error controlling mute:', err);
    }
  }, [isMuted, debugLog, onMuteStateChange]);

  // Progress bar handlers
  const handleProgressBarClick = (e) => {
    if (!player || !duration) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = (clickX / rect.width) * 100;
    const newTime = (percentage / 100) * duration;
    
    safePlayerCall('seekTo', newTime, true);
    setProgress(percentage);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear progress tracking interval on unmount
      if (progressIntervalRef.current) {
        clearInterval(progressIntervalRef.current);
        progressIntervalRef.current = null;
      }
    };
  }, []); // Empty dependency array - only cleanup on true unmount

  if (!shortData || !videoId) {
    return (
      <div className="shorts-player-error">
        <p>No video data available</p>
      </div>
    );
  }

  if (error) {
    // Handle both string errors and object errors
    const isAutoplayBlocked = typeof error === 'object' && error.type === 'autoplay-blocked';
    const errorMessage = typeof error === 'string' ? error : error.message || 'Video unavailable';
    const errorDetail = typeof error === 'object' ? error.detail : null;
    
    return (
      <div 
        className={`shorts-player-error ${isAutoplayBlocked ? 'autoplay-blocked' : ''}`}
        onClick={() => {
          if (isAutoplayBlocked && player && player.playVideo) {
            console.log('User tapped to play video');
            setError(null); // Clear the error
            player.mute(); // Ensure muted
            player.playVideo(); // Start playing
            onFirstInteraction && onFirstInteraction(); // Mark user interaction
          }
        }}
      >
        {isAutoplayBlocked && (
          <div className="play-icon">▶</div>
        )}
        <p>{errorMessage}</p>
        {errorDetail && (
          <p className="error-detail">{errorDetail}</p>
        )}
        {!isAutoplayBlocked && (
          <p>Video may be private or unavailable</p>
        )}
      </div>
    );
  }

  // Debug log to track button rendering
  // console.log('ShortsPlayerYouTube rendering - hasUserUnlockedSound:', hasUserUnlockedSound, 'isMuted:', isMuted);

  return (
    <div className="shorts-player-container">
      <div className={`shorts-player ${!isPlaying ? 'paused' : ''}`}>
        {/* YouTube Player Container */}
        <div 
          ref={containerRef}
          className="youtube-player-container"
          style={{ width: '100%', height: '100%' }}
          data-allow="autoplay; fullscreen; encrypted-media"
        />
        
        {/* Loading state */}
        {!isReady && !error && (
          <div className="player-placeholder">
            <div className="loading-spinner"></div>
            <p>Loading video...</p>
          </div>
        )}
        
        {/* Progress bar */}
        <div 
          className="video-progress-bar"
          onClick={handleProgressBarClick}
        >
          <div 
            className="video-progress-fill" 
            style={{ '--progress-percent': `${progress}%` }}
          />
          <div 
            className="video-progress-handle"
            style={{ left: `${progress}%` }}
          />
        </div>
        
        {/* Audio Control Wrapper - ensures proper positioning and visibility */}
        <div className="audio-control-wrapper">
          <div className="action-item audio-action">
            <button 
              className={`audio-control-btn ${!isMuted ? 'unmuted' : ''}`}
              onClick={(e) => {
                e.stopPropagation();
                debugLog('Mute button clicked', { 
                  currentMuted: isMuted,
                  actualMuteState,
                  apiMuted: player?.isMuted ? player.isMuted() : 'N/A'
                });
                
                // Capture first user interaction for sound unlock
                if (!hasUserUnlockedSound && isMuted) {
                  debugLog('First user interaction - unlocking sound');
                  hasUserUnlockedSound = true;
                  window.hasUserUnlockedSound = true;
                  if (onFirstInteraction) {
                    onFirstInteraction();
                  }
                }
                
                // Mark session interaction
                if (!sessionUserInteraction) {
                  debugLog('Marking session user interaction');
                  sessionUserInteraction = true;
                  window.sessionUserInteraction = true;
                }
                
                // Update preferences
                lastUserMutePreference = !isMuted;
                window.lastUserMutePreference = lastUserMutePreference;
                
                // Save preferences to localStorage
                saveAudioPreferences(hasUserUnlockedSound, lastUserMutePreference);
                
                // Update actual mute state
                const targetMuteState = !actualMuteState;
                actualMuteState = targetMuteState;
                window.actualMuteState = actualMuteState;
                
                // If unmuting, use enhanced retry logic
                if (player && isPlayerReady && isMuted) {
                  debugLog('Attempting to unmute with retry logic');
                  
                  // First attempt
                  player.unMute();
                  player.setVolume(75);
                  
                  // Check and retry multiple times
                  let retryCount = 0;
                  const maxRetries = 5;
                  
                  const checkAndResume = setInterval(() => {
                    if (!player || !isPlayerReady) {
                      clearInterval(checkAndResume);
                      return;
                    }
                    
                    const playerState = player?.getPlayerState?.() || 0;
                    const apiMuted = player?.isMuted?.() || true;
                    
                    debugLog(`Unmute check ${retryCount + 1}`, {
                      playerState,
                      apiMuted,
                      isPlaying
                    });
                    
                    // If still muted, force unmute again
                    if (apiMuted && retryCount < maxRetries) {
                      player.unMute();
                      player.setVolume(75);
                    }
                    
                    // If video paused, resume playback
                    if (playerState !== 1 && isPlaying) { // Not playing
                      debugLog('Video paused after unmute, resuming');
                      player.playVideo();
                    }
                    
                    retryCount++;
                    
                    if (retryCount >= maxRetries || (!apiMuted && playerState === 1)) {
                      clearInterval(checkAndResume);
                      debugLog('Unmute retry complete', {
                        success: !apiMuted && playerState === 1,
                        finalApiMuted: apiMuted,
                        finalPlayerState: playerState
                      });
                    }
                  }, 200);
                } else if (player && isPlayerReady && !isMuted) {
                  // Muting
                  debugLog('Muting player');
                  player.mute();
                }
                
                // Notify parent component
                onMuteStateChange(!isMuted);
              }}
            >
              {isMuted ? (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="white" style={{ pointerEvents: 'none' }}>
                  <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
                </svg>
              ) : (
                <svg width="24" height="24" viewBox="0 0 24 24" fill="white" style={{ pointerEvents: 'none' }}>
                  <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
                </svg>
              )}
            </button>
          </div>
        </div>
        
        {/* Navigation Controls */}
        <div className="video-nav-controls">
            <button 
              className="nav-control nav-prev"
              onClick={(e) => {
                e.stopPropagation();
                onPrevVideo?.();
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
              </svg>
            </button>

            <button 
              className="nav-control nav-play-pause"
              onClick={(e) => {
                e.stopPropagation();
                
                // Mark session interaction when user pauses/plays
                if (!sessionUserInteraction) {
                  debugLog('Marking session user interaction from play/pause');
                  sessionUserInteraction = true;
                  window.sessionUserInteraction = true;
                }
                
                onPlayStateChange(!isPlaying);
              }}
            >
              {isPlaying ? (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                  <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>
                </svg>
              ) : (
                <svg width="32" height="32" viewBox="0 0 24 24" fill="white">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              )}
            </button>

            <button 
              className="nav-control nav-next"
              onClick={(e) => {
                e.stopPropagation();
                onNextVideo?.();
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
                <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
              </svg>
            </button>
          </div>
        
        {/* Time display */}
        {duration > 0 && (
          <div className="video-time-display">
            {formatTime(progress * duration / 100)} / {formatTime(duration)}
          </div>
        )}
      </div>
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders when parent updates
// Only re-render when shortData actually changes (different video)
export default React.memo(ShortsPlayerYouTube, (prevProps, nextProps) => {
  // Return true if props are equal (skip re-render)
  // Return false if props changed (do re-render)
  
  // Check if video ID changed
  const prevVideoId = prevProps.shortData ? extractVideoId(prevProps.shortData.shortUrl) : null;
  const nextVideoId = nextProps.shortData ? extractVideoId(nextProps.shortData.shortUrl) : null;
  
  if (prevVideoId !== nextVideoId) {
    return false; // Video changed, need to re-render
  }
  
  // Check other important props
  if (prevProps.isPlaying !== nextProps.isPlaying ||
      prevProps.isMuted !== nextProps.isMuted) {
    return false; // Control state changed, need to re-render
  }
  
  // Props are effectively the same, skip re-render
  return true;
});